import React, { useState } from 'react';
import {
  PhotoIcon,
  CheckIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Modal from '../ui/Modal';

interface BulkOperationsProps {
  selectedProductIds: string[];
  selectedProducts: Array<{id: string; images: any[]}>;
  onBulkImportImages: (productIds: string[]) => Promise<void>;
  onClearSelection: () => void;
}

interface BulkOperation {
  type: 'images';
  title: string;
  description: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  color: string;
}

const BulkOperations: React.FC<BulkOperationsProps> = ({
  selectedProductIds,
  selectedProducts,
  onBulkImportImages,
  onClearSelection,
}) => {
  const [activeOperation, setActiveOperation] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [formValue, setFormValue] = useState('');

  const operations: BulkOperation[] = [
    {
      type: 'images',
      title: 'Atualizar Imagens',
      description: 'Buscar imagens atualizadas da plataforma de origem',
      icon: PhotoIcon,
      color: 'blue',
    },
  ];

  const handleOperationClick = (operationType: string) => {
    setActiveOperation(operationType);
    setFormValue('');
  };

  const handleCloseModal = () => {
    setActiveOperation(null);
    setFormValue('');
    setIsLoading(false);
  };

  const handleConfirmOperation = async () => {
    if (!activeOperation) return;

    try {
      setIsLoading(true);

      switch (activeOperation) {
        case 'images':
          await onBulkImportImages(selectedProductIds);
          break;
      }

      handleCloseModal();
      onClearSelection();
    } catch (error) {
      console.error('Erro na operação em lote:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const availableOperations = operations;

  const currentOperation = availableOperations.find(op => op.type === activeOperation);
  const needsInput = false; // Novas operações não precisam input

  if (selectedProductIds.length === 0) {
    return null;
  }

  return (
    <>
      {/* Barra flutuante com operações */}
      <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50">
        <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-4">
          <div className="flex items-center space-x-4">
            {/* Contador de selecionados */}
            <div className="flex items-center space-x-2">
              <div className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-semibold">
                {selectedProductIds.length} selecionado{selectedProductIds.length !== 1 ? 's' : ''}
              </div>
              <button
                onClick={onClearSelection}
                className="text-gray-400 hover:text-red-600 transition-colors"
                title="Limpar seleção"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            {/* Separador */}
            <div className="w-px h-6 bg-gray-300"></div>

            {/* Botões de operações */}
            <div className="flex items-center space-x-2">
              {availableOperations.map((operation) => {
                const Icon = operation.icon;
                return (
                  <button
                    key={operation.type}
                    onClick={() => handleOperationClick(operation.type)}
                    className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      operation.color === 'green'
                        ? 'bg-green-50 text-green-700 hover:bg-green-100'
                        : operation.color === 'blue'
                        ? 'bg-blue-50 text-blue-700 hover:bg-blue-100'
                        : 'bg-purple-50 text-purple-700 hover:bg-purple-100'
                    }`}
                    title={operation.description}
                  >
                    <Icon className="h-4 w-4" />
                    <span>{operation.title}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Modal de confirmação */}
      <Modal
        isOpen={!!activeOperation}
        onClose={handleCloseModal}
        title={currentOperation?.title || ''}
        size="md"
      >
        <div className="space-y-4">
          {/* Ícone e descrição */}
          <div className="flex items-center space-x-3">
            {currentOperation && (
              <>
                <div className={`p-3 rounded-full ${
                  currentOperation.color === 'green'
                    ? 'bg-green-100'
                    : currentOperation.color === 'blue'
                    ? 'bg-blue-100'
                    : 'bg-purple-100'
                }`}>
                  <currentOperation.icon className={`h-6 w-6 ${
                    currentOperation.color === 'green'
                      ? 'text-green-600'
                      : currentOperation.color === 'blue'
                      ? 'text-blue-600'
                      : 'text-purple-600'
                  }`} />
                </div>
                <div>
                  <h3 className="text-lg font-medium text-gray-900">
                    {currentOperation.title}
                  </h3>
                  <p className="text-sm text-gray-500">
                    {currentOperation.description}
                  </p>
                </div>
              </>
            )}
          </div>

          {/* Aviso sobre produtos afetados */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
            <div className="flex items-center space-x-2">
              <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600" />
              <span className="text-sm text-yellow-800">
                Esta operação afetará <strong>{selectedProductIds.length}</strong> produto
                {selectedProductIds.length !== 1 ? 's' : ''} selecionado
                {selectedProductIds.length !== 1 ? 's' : ''}
              </span>
            </div>
          </div>


          {/* Botões de ação */}
          <div className="flex justify-end space-x-3 pt-4">
            <Button
              variant="secondary"
              onClick={handleCloseModal}
              disabled={isLoading}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleConfirmOperation}
              disabled={isLoading}
              className={`${
                currentOperation?.color === 'green'
                  ? 'bg-green-600 hover:bg-green-700'
                  : currentOperation?.color === 'blue'
                  ? 'bg-blue-600 hover:bg-blue-700'
                  : 'bg-purple-600 hover:bg-purple-700'
              }`}
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Processando...
                </>
              ) : (
                <>
                  <CheckIcon className="h-4 w-4 mr-2" />
                  Confirmar
                </>
              )}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default BulkOperations;